import { useEffect, useMemo, useRef, useState } from "react";

import {
  Notification,
  NotificationType,
  SlashCircleIcon,
} from "@transfr-inc/dashboard-components";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";
import {
  handleSort,
  SortDirection,
} from "@transfr-inc/dashboard-components/data-table";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { CourseGradebookFilter } from "@components/custom/forms";
import { ShortcutAction } from "@components/shortcuts";
import { doExportData, ExportStatus } from "@lib/export-utils";
import { generateGradebookCSVData } from "@lib/gradebook-export.utils";
import { useApiRequest } from "@lib/hooks";
import { OohStatus } from "@models/ooh-status";
import container from "@src/container";
import { CourseGradebookTable } from "@views/classrooms/details/components/course.gradebook-table";
import { setMasteryInfoOnActivities } from "@views/my-work/components/course/course.utils";

import "./course.gradebook.scss";

export const CourseGradebook = ({
  classroom,
  users,
  selectedUser,
  activityStatus,
  readOnly,
}) => {
  const [filteredCourseData, setFilteredCourseData] = useState();
  const [gradebookData, setGradebookData] = useState();
  const [defaultUnit, setDefaultUnit] = useState();
  const [activitiesData, setActivitiesData] = useState();
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [exportStatus, setExportStatus] = useState(ExportStatus.complete);
  const [notification, setNotification] = useState();
  const [updatedMasteryInfo, setUpdatedMasteryInfo] = useState();
  const lastProcessedActivityStatusRef = useRef(null);
  const filterCriteriaRef = useRef(null);

  const { insightsService, courseService } = container;
  const {
    loading: loadingMasteryInfo,
    response: masteryInfo,
    sendRequest: fetchMasteryInfo,
  } = useApiRequest(
    () =>
      insightsService
        .getClassroomProgramMasteryInfo(
          classroom.courseId,
          users.map((u) => u.userId),
          classroom.classroomId
        )
        .catch(() => new Map()),
    false
  );

  useEffect(() => {
    if (users.length > 0) {
      fetchMasteryInfo();
    } else {
      // Nothing to fetch for a classroom with no learners. Seed empty mastery
      // so the course structure still resolves instead of spinning forever.
      setUpdatedMasteryInfo({ userActivityMetrics: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const { response: data, loading: loadingCourseData } = useApiRequest(() =>
    courseService.getCourseDetails(classroom.courseId)
  );

  const { response: activities, loading: loadingActivities } = useApiRequest(
    () => courseService.getCourseActivities(classroom.courseId)
  );

  const baseMasteryInfo = updatedMasteryInfo ?? masteryInfo;

  useEffect(() => {
    if (notification) {
      const timoutId = setTimeout(() => setNotification(), 3000);
      return () => clearTimeout(timoutId);
    }
  }, [notification]);

  const addMasteryInfoToFilters = (fakeMasteryInfo, type) => {
    if (type === "unit" && filters?.unit?.users) {
      const updatedUsers = filters.unit.users.map((user) =>
        user.userId === fakeMasteryInfo.userId
          ? { ...user, [fakeMasteryInfo.objectId]: fakeMasteryInfo }
          : user
      );
      return { ...filters.unit, users: updatedUsers };
    } else if (type === "user" && filters?.users) {
      return filters.users.map((user) =>
        user.userId === fakeMasteryInfo.userId
          ? { ...user, [fakeMasteryInfo.objectId]: fakeMasteryInfo }
          : user
      );
    }
    return type === "unit" ? filters?.unit : filters?.users;
  };

  useEffect(() => {
    if (masteryInfo && activities) {
      const activityStatusString = `${activityStatus?.user_id}-${activityStatus?.activity_id}`;
      if (
        baseMasteryInfo &&
        activityStatus &&
        activityStatusString !== lastProcessedActivityStatusRef.current
      ) {
        if (activityStatus?.complete && activityStatus?.user_id) {
          // Find the parent activity to get lessonId and unitId
          const parentActivity = activities.find(
            (activity) => activity.activityId === activityStatus.activity_id
          );

          const fakeMasteryInfo = {
            attempts: 0,
            courseId: classroom.courseId,
            exempt: false,
            completed: true,
            passStatus: true,
            latestTimestamp: new Date().toISOString(),
            lessonId: parentActivity?.lessonId || "",
            activityId: activityStatus.activity_id,
            objectId: activityStatus.activity_id,
            unitId: parentActivity?.unitId || "",
            userId: activityStatus.user_id,
            contentId: activityStatus.activity_id,
            masteryStatus: OohStatus.PASSED,
            status: "complete",
          };

          // Add the fake mastery info to the unified userActivityMetrics rows
          const updatedMastery = {
            ...baseMasteryInfo,
            userActivityMetrics: [
              ...(baseMasteryInfo.userActivityMetrics || []),
              fakeMasteryInfo,
            ],
          };
          setUpdatedMasteryInfo(updatedMastery);

          const currentFilters = {
            unit: addMasteryInfoToFilters(fakeMasteryInfo, "unit"),
            lessons: filters?.lessons,
            users: addMasteryInfoToFilters(fakeMasteryInfo, "user"),
          };

          setFilters(currentFilters);
          filterCriteriaRef.current = currentFilters;

          lastProcessedActivityStatusRef.current = activityStatusString;
        }
      } else {
        setUpdatedMasteryInfo(baseMasteryInfo);
      }
    }
  }, [activityStatus, masteryInfo, activities]);

  useEffect(() => {
    if (
      !loadingMasteryInfo &&
      !loadingCourseData &&
      !loadingActivities &&
      updatedMasteryInfo
    ) {
      const { defaultUnit, unitMap, activitiesWithMasteryInfo } =
        setMasteryInfoOnActivities(activities, updatedMasteryInfo, users);

      setGradebookData(unitMap);

      setDefaultUnit(defaultUnit);
      setActivitiesData(activitiesWithMasteryInfo);
      setLoading(false);
    }
  }, [
    updatedMasteryInfo,
    data,
    activities,
    users,
    loadingMasteryInfo,
    loadingCourseData,
    loadingActivities,
  ]);

  const onSortClick = ({ column, sortDirection }) => {
    const sortedData = handleSort(
      activitiesData,
      column,
      "isAnonymous",
      sortDirection || SortDirection.ASCENDING
    );
    setActivitiesData(sortedData);
  };

  useEffect(() => () => insightsService.cancelRequest(), []);

  // Update filteredCourseData when filters change (user manually applies filters)
  useEffect(() => {
    if (!gradebookData) return;

    const unitId = filters?.unit?.unitId ?? defaultUnit?.unitId;
    if (!unitId) return;

    const unitFromMap = gradebookData.get(unitId);
    if (!unitFromMap) return;

    // Refresh the filtered users from the latest gradebook data so newly
    // injected mastery entries are picked up without requiring a manual filter apply.
    const selectedUsers =
      filters?.users?.length > 0 ? filters.users : unitFromMap.users || [];
    const selectedUserIds = new Set(selectedUsers.map((user) => user.userId));
    const refreshedUsers = (unitFromMap.users || []).filter((user) =>
      selectedUserIds.has(user.userId)
    );

    setFilteredCourseData(refreshedUsers);
  }, [gradebookData, filters, defaultUnit]);

  const onApplyFilters = (filtersData) => {
    if (filterCriteriaRef.current) {
      setFilters(filterCriteriaRef.current);
      filterCriteriaRef.current = null;
    } else {
      setFilters(filtersData);
    }
  };

  const onExportData = async () => {
    setExportStatus(ExportStatus.creating);

    try {
      if (!data || !filteredCourseData || !filteredActivities?.length) {
        console.error("No data available for export");
        setExportStatus(ExportStatus.error);
        return;
      }

      const programName = data?.title || "";

      const csvData = generateGradebookCSVData(
        filteredCourseData,
        filteredActivities,
        programName
      );

      const fileName = `${classroom.name} - Gradebook`;
      await doExportData(fileName, csvData);
      setExportStatus(ExportStatus.complete);
    } catch {
      setExportStatus(ExportStatus.error);
      setNotification({
        type: NotificationType.error,
        message: "Something went wrong while exporting the data.",
        closable: true,
      });
    }
  };

  const filteredActivities = useMemo(() => {
    return (filters.lessons || []).flatMap((lesson) => lesson.activities);
  }, [filters]);

  const isFilteringByUnits = useMemo(() => {
    return filters.unit;
  }, [filters]);

  const hasUnits = useMemo(() => {
    return (
      filteredCourseData &&
      filteredCourseData?.units &&
      filteredCourseData?.units?.length > 0
    );
  }, [filteredCourseData]);

  const dataTableKey = useMemo(() => {
    const gradeValuesHash = gradebookData
      ? [...gradebookData.values()].map(JSON.stringify).join("|")
      : "";

    return `${baseMasteryInfo?.userActivityMetrics?.length ?? 0}-${gradeValuesHash}`;
  }, [baseMasteryInfo, gradebookData]);

  return (
    <div className="classroom-gradebook-tab">
      <div className="header-container">
        <div className="gradebook-filter-container">
          <CourseGradebookFilter
            onApplyFilters={onApplyFilters}
            gradebookData={gradebookData}
            defaultUnit={defaultUnit}
            selectedUser={selectedUser}
          ></CourseGradebookFilter>
          {filters.unit && (
            <div className="filter-module">
              {filters.unit.isVirtualUnit ? "Lesson" : "Unit"}:{" "}
              <span>{filters?.unit?.title}</span>
            </div>
          )}
        </div>
        <div className="gradebook-buttons-container">
          <Button onClick={() => console.log("clicked")}>Feedback</Button>
          <Button
            icon={["fa-regular", "file-export"]}
            onClick={onExportData}
            primary
            disabled={exportStatus === ExportStatus.creating}
          >
            {exportStatus === ExportStatus.creating ? "Exporting..." : "Export"}
          </Button>
        </div>
      </div>
      <div className="gradebook-table-container">
        {users.length === 0 && (
          <ShortcutAction className="no-data-message" disabled>
            <SlashCircleIcon></SlashCircleIcon>
            There are no active {getLearnerTerm({ plural: true })} for this
            classroom.
          </ShortcutAction>
        )}
        <CourseGradebookTable
          data={filteredCourseData}
          activities={filteredActivities}
          classroom={classroom}
          isLoading={loading}
          onSortClick={onSortClick}
          forceNoUnitHierarchy={isFilteringByUnits}
          hasUnits={hasUnits}
          readOnly={readOnly}
          dataTableKey={dataTableKey}
        ></CourseGradebookTable>
      </div>
      {notification && (
        <Notification
          type={notification.type}
          icon={notification.icon}
          closable
          onClose={() => setNotification()}
        >
          {notification.message}
        </Notification>
      )}
    </div>
  );
};
